import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { useState } from "react";
import { ScanLine, TrendingUp, Clock, Download, BarChart3, Trash2, CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, toYMD } from "@/lib/apiClient";

const StatCard = ({ label, value, icon: Icon, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-border bg-card p-5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className="mt-2 font-display text-4xl font-extrabold tracking-tighter">{value}</div>
  </div>
);

const fmtDate = (ts) =>
  ts
    ? new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "Never";

const shortDate = (d) => (d ? d.toLocaleDateString([], { month: "short", day: "numeric" }) : null);

const DatePickerButton = ({ label, value, onSelect, disabled, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full font-normal"
          data-testid={testid}
        >
          <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
          {value ? shortDate(value) : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(d) => { onSelect(d); setOpen(false); }}
          disabled={disabled}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export const AnalyticsPanel = ({ data, onClearScans, range, onRangeChange, qrId }) => {
  const [clearRange, setClearRange] = useState("all");
  if (!data) return null;
  const topEvent = data.per_event?.[0];
  const maxScans = Math.max(1, ...(data.per_event || []).map((e) => e.scans));

  const from = range?.from;
  const to = range?.to;
  const rangeActive = !!(from && to);
  const csvRange = rangeActive ? { start: toYMD(from), end: toYMD(to) } : undefined;

  const downloadCsv = (eventId) => {
    api.downloadCsv(eventId, csvRange, qrId);
  };

  const confirmClear = () => {
    const days = clearRange === "all" ? null : Number(clearRange);
    onClearScans(days);
  };

  const chartTitle = rangeActive
    ? `Scans · ${shortDate(from)} – ${shortDate(to)}`
    : "Scans · last 14 days";

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3"
        data-testid="date-range-toolbar"
      >
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Date range</span>
        <div className="flex items-center gap-2">
          <DatePickerButton
            label="From"
            value={from}
            onSelect={(d) => onRangeChange({ from: d, to })}
            disabled={(d) => (to ? d > to : false) || d > new Date()}
            testid="range-from-btn"
          />
          <span className="text-muted-foreground text-sm">–</span>
          <DatePickerButton
            label="To"
            value={to}
            onSelect={(d) => onRangeChange({ from, to: d })}
            disabled={(d) => (from ? d < from : false) || d > new Date()}
            testid="range-to-btn"
          />
          {rangeActive && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full text-muted-foreground"
              onClick={() => onRangeChange({ from: undefined, to: undefined })}
              data-testid="range-clear-btn"
            >
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="grid grid-cols-2 gap-4 flex-1 mr-4">
          <StatCard label="Total scans" value={data.total_scans} icon={ScanLine} testid="stat-total-scans" />
          <StatCard
            label="Top event"
            value={topEvent && topEvent.scans > 0 ? topEvent.name : "—"}
            icon={TrendingUp}
            testid="stat-top-event"
          />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-4">
          <TrendingUp className="h-3.5 w-3.5" /> {chartTitle}
        </div>
        <div className="h-52 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.timeline} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <defs>
                <linearGradient id="scanFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} width={30} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 12,
                  color: "hsl(var(--foreground))",
                }}
              />
              <Area type="monotone" dataKey="scans" stroke="hsl(var(--accent))" strokeWidth={2.5} fill="url(#scanFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Event-wise breakdown */}
      <div className="rounded-xl border border-border bg-card p-5" data-testid="event-breakdown">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <BarChart3 className="h-3.5 w-3.5" /> Scans by event
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="export-csv-all-btn"
              onClick={() => downloadCsv(null)}
              variant="outline"
              size="sm"
              className="rounded-full active:scale-95 transition-transform duration-150"
            >
              <Download className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="clear-data-btn"
                  variant="outline"
                  size="sm"
                  className="rounded-full text-destructive hover:text-destructive active:scale-95 transition-transform duration-150"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Clear data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="clear-data-dialog" className="rounded-2xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete scan history</AlertDialogTitle>
                  <AlertDialogDescription>
                    Choose how much scan data to remove. This can't be undone. Your events and phone number stay intact.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-2">
                  <Select value={clearRange} onValueChange={setClearRange}>
                    <SelectTrigger data-testid="clear-range-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="clear-range-all">All scan history</SelectItem>
                      <SelectItem value="90" data-testid="clear-range-90">Older than 90 days</SelectItem>
                      <SelectItem value="30" data-testid="clear-range-30">Older than 30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="clear-data-confirm-btn"
                    onClick={confirmClear}
                    className="rounded-full bg-destructive hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        {data.per_event?.length ? (
          <ul className="space-y-4">
            {data.per_event.map((e) => (
              <li key={e.event_id} data-testid={`breakdown-row-${e.event_id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="font-display font-bold tracking-tight truncate">{e.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {e.today > 0 ? `+${e.today} today · ` : ""}last {fmtDate(e.last_scanned)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-display font-extrabold tabular-nums">{e.scans}</span>
                    <span className="text-xs text-muted-foreground w-9 text-right">{e.percent}%</span>
                    <button
                      data-testid={`export-csv-event-${e.event_id}`}
                      onClick={() => downloadCsv(e.event_id)}
                      title={`Export ${e.name} scans`}
                      className="text-muted-foreground hover:text-foreground transition-colors duration-150"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
                  <div
                    className="h-full rounded-full bg-accent transition-[width] duration-500"
                    style={{ width: `${(e.scans / maxScans) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No events to break down yet.</p>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-5" data-testid="recent-scans">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-4">
          <Clock className="h-3.5 w-3.5" /> Recent scans
        </div>
        {data.recent?.length ? (
          <ul className="space-y-2.5">
            {data.recent.map((s) => (
              <li key={s.id} className="flex items-center justify-between text-sm gap-3">
                <div className="min-w-0">
                  <span className="font-medium">{s.event_name}</span>
                  {(s.contact_name || s.contact_phone) && (
                    <span className="text-xs text-muted-foreground ml-2" data-testid={`scan-contact-${s.id}`}>
                      · {s.contact_name || "Visitor"}{s.contact_phone ? ` (${s.contact_phone})` : ""}
                    </span>
                  )}
                </div>
                <span className="text-muted-foreground text-xs shrink-0">{fmtDate(s.timestamp)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No scans yet. Share your QR to start tracking.</p>
        )}
      </div>
    </div>
  );
};
